import { isOperatorRole } from '@app/shared'
import { useMutation } from '@tanstack/react-query'
import { ArrowRight, CircleAlert, Eye, EyeOff, KeyRound, LockKeyhole, Mail, ShieldCheck, WifiOff } from 'lucide-react'
import { type FormEvent, type KeyboardEvent, useState } from 'react'

import { getCurrentUser, signIn } from '../api/admin'
import { BrandMark } from '../components/layout/BrandMark'
import { Spinner } from '../components/ui/primitives'
import { ApiError, NetworkError } from '../lib/http'
import { clearTokens } from '../lib/session'
import styles from './SignIn.module.css'

function messageFor(error: unknown): string {
  if (error instanceof NetworkError) return 'Нет связи с сервером'
  if (error instanceof ApiError) {
    if (error.status === 429) return 'Слишком много попыток. Подождите и попробуйте снова'
    if (error.code === 'TOTP_REQUIRED') return 'Введите код из приложения-аутентификатора'
    if (error.code === 'TOTP_INVALID' || error.code === 'TOTP_REPLAYED') return 'Код не подошёл. Проверьте время на телефоне и введите новый'
    if (error.status === 401 || error.status === 400) return 'Неверная почта или пароль'
    if (error.code === 'FORBIDDEN_ROLE') return 'У этой учётной записи нет доступа к панели'
  }
  return 'Не удалось войти'
}

/** The server wants a code (asked for one, or the one sent did not fit). */
function asksForCode(error: unknown): boolean {
  return error instanceof ApiError && error.code.startsWith('TOTP_')
}

function codeRejected(error: unknown): boolean {
  return error instanceof ApiError && (error.code === 'TOTP_INVALID' || error.code === 'TOTP_REPLAYED')
}

function credentialsRejected(error: unknown): boolean {
  return error instanceof ApiError && !error.code.startsWith('TOTP_') && error.code !== 'FORBIDDEN_ROLE' && (error.status === 401 || error.status === 400)
}

const CODE_CELLS = 6

export default function SignIn({ onSignedIn }: { onSignedIn: () => void }): JSX.Element {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [reveal, setReveal] = useState(false)
  const [capsLock, setCapsLock] = useState(false)
  // Whether the last answer asked for a code. Kept while the next attempt is
  // in flight, so the field does not blink away under «Проверяем…».
  const [codeStep, setCodeStep] = useState(false)

  const mutation = useMutation({
    mutationFn: async (): Promise<void> => {
      await signIn(email.trim(), password, totpCode.trim() === '' ? undefined : totpCode.trim())
      // The login endpoint is shared with the customer app, so the panel checks
      // the role itself and drops the session rather than rendering an operator
      // interface to somebody who is not one.
      const me = await getCurrentUser()
      if (!isOperatorRole(me.user.role)) {
        clearTokens()
        throw new ApiError(403, 'FORBIDDEN_ROLE', 'not an operator')
      }
    },
    onError: (error) => setCodeStep(asksForCode(error)),
    onSuccess: onSignedIn,
  })

  function submit(event: FormEvent): void {
    event.preventDefault()
    if (email.trim() === '' || password === '') return
    mutation.mutate()
  }

  const onPasswordKey = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (typeof event.getModifierState === 'function') setCapsLock(event.getModifierState('CapsLock'))
  }

  const error = mutation.isError ? mutation.error : null
  const showCode = codeStep || asksForCode(error)
  const badCredentials = credentialsRejected(error)
  const badCode = codeRejected(error)
  // «Enter the code» is a prompt, not a failure.
  const tone = error instanceof ApiError && error.code === 'TOTP_REQUIRED' ? 'info' : 'down'
  const ErrorIcon = error instanceof NetworkError ? WifiOff : tone === 'info' ? KeyRound : CircleAlert

  return (
    <div className={styles.page}>
      <div className={styles.backdrop} aria-hidden="true">
        <div className={styles.grid} />
        <div className={styles.glow} />
        <div className={styles.glowSecond} />
      </div>

      <main className={styles.center}>
        <div className={styles.card}>
          <div className={styles.brand}>
            <BrandMark size={8} />
            <span className={styles.brandName}>19CRT</span>
            <span className={styles.brandChip}>Админ</span>
          </div>

          <div className={styles.heading}>
            <h1 className={styles.title}>{showCode ? 'Подтвердите вход' : 'Вход в панель'}</h1>
            <p className={styles.subtitle}>
              {showCode ? 'Для этой учётной записи включена двухфакторная защита.' : 'Доступ только для операторов платформы.'}
            </p>
          </div>

          <form className={styles.form} onSubmit={submit}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="email">
                Почта
              </label>
              <div className={styles.control}>
                <Mail size={16} strokeWidth={1.8} className={styles.controlIcon} aria-hidden="true" />
                <input
                  id="email"
                  className={styles.input}
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  aria-invalid={badCredentials || undefined}
                  autoFocus
                  required
                />
              </div>
            </div>

            <div className={styles.field}>
              <div className={styles.labelRow}>
                <label className={styles.label} htmlFor="password">
                  Пароль
                </label>
                {capsLock ? <span className={styles.caps}>Включён Caps Lock</span> : null}
              </div>
              <div className={styles.control}>
                <LockKeyhole size={16} strokeWidth={1.8} className={styles.controlIcon} aria-hidden="true" />
                <input
                  id="password"
                  className={`${styles.input} ${styles.withAction}`}
                  type={reveal ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  onKeyUp={onPasswordKey}
                  onKeyDown={onPasswordKey}
                  onBlur={() => setCapsLock(false)}
                  aria-invalid={badCredentials || undefined}
                  required
                />
                <button
                  type="button"
                  className={styles.reveal}
                  onClick={() => setReveal((value) => !value)}
                  aria-label={reveal ? 'Скрыть пароль' : 'Показать пароль'}
                  aria-pressed={reveal}
                  title={reveal ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  {reveal ? <EyeOff size={16} strokeWidth={1.8} aria-hidden="true" /> : <Eye size={16} strokeWidth={1.8} aria-hidden="true" />}
                </button>
              </div>
            </div>

            {showCode ? (
              <div className={`${styles.field} ${styles.codeField}`}>
                <label className={styles.label} htmlFor="totpCode">
                  Код подтверждения
                </label>
                <CodeInput value={totpCode} onChange={setTotpCode} invalid={badCode} attempt={mutation.submittedAt} />
                <p className={styles.codeHint}>Код в приложении обновляется каждые 30 секунд.</p>
              </div>
            ) : null}

            {error ? (
              <p key={mutation.submittedAt} className={styles.message} data-tone={tone} role="alert">
                <ErrorIcon size={16} strokeWidth={1.9} aria-hidden="true" className={styles.messageIcon} />
                <span>{messageFor(error)}</span>
              </p>
            ) : null}

            <button className={styles.submit} type="submit" disabled={mutation.isPending} aria-busy={mutation.isPending || undefined}>
              {mutation.isPending ? (
                <>
                  <Spinner size={15} />
                  <span>Проверяем…</span>
                </>
              ) : (
                <>
                  <span>Войти</span>
                  <ArrowRight size={16} strokeWidth={2} aria-hidden="true" className={styles.submitArrow} />
                </>
              )}
            </button>
          </form>

          <p className={styles.note}>
            <ShieldCheck size={16} strokeWidth={1.8} aria-hidden="true" className={styles.noteIcon} />
            <span>
              Двухфакторная аутентификация обязательна для одобрения выводов. Она подключается в профиле после первого
              входа.
            </span>
          </p>
        </div>

        <p className={styles.footer}>19CRT · операторская консоль</p>
      </main>
    </div>
  )
}

/**
 * The code as six cells. One real input lies over them (transparent), so
 * typing, pasting, autofill of one-time codes and screen readers all work on
 * a plain field; the cells only draw its value. More than six digits (up to
 * the field's eight) add cells rather than being cut.
 */
function CodeInput({ value, onChange, invalid, attempt }: { value: string; onChange: (next: string) => void; invalid: boolean; attempt: number }): JSX.Element {
  const [focused, setFocused] = useState(false)
  const cells = Math.max(CODE_CELLS, value.length)
  const current = value.length < cells ? value.length : -1
  return (
    <div className={styles.code} data-invalid={invalid || undefined}>
      <div key={invalid ? attempt : 'ok'} className={styles.cells} data-shake={invalid || undefined} aria-hidden="true">
        {Array.from({ length: cells }, (_, index) => (
          <span key={index} className={styles.cell} data-filled={index < value.length || undefined} data-current={(focused && index === current) || undefined}>
            {value[index] !== undefined ? <span className={styles.digit}>{value[index]}</span> : null}
          </span>
        ))}
      </div>
      <input
        id="totpCode"
        className={styles.codeInput}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={8}
        autoFocus
        value={value}
        onChange={(event) => onChange(event.target.value.replace(/\D/g, ''))}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        aria-invalid={invalid || undefined}
      />
    </div>
  )
}
